/*
  Zen Gift Guard
  ==============
  Single source of truth for the rule "one free-gift set per qualifying offer".

  WHY THIS EXISTS (Aug 2026)
  Free gifts were reaching checkout at quantity 2+ (Jose's report + the order
  screenshots). Three independent paths allowed it:

    1. /cart rendered a bare <input type="number"> on gift lines, so a customer
       could type any quantity and press "Update cart".
    2. /cart/add.js MERGES an add into an existing line when variant +
       properties + selling_plan all match. Every buy-box CTA appends its gift
       lines unconditionally, so clicking a CTA twice doubled every gift.
    3. The cart drawer's +/- buttons applied to gift lines like any other line.

  Patching each surface separately is whack-a-mole, and none of it survives a
  direct POST to /cart/change.js. So instead of trusting the UI, this
  reconciles the WHOLE cart after every cart mutation and immediately before
  every checkout hand-off.

  SAFETY PROPERTY
  The reconciler only ever REDUCES a gift line's quantity. It never raises one
  and never adds a line, so it cannot invent an entitlement the customer did
  not already have. A bug here can under-gift, never over-charge.

  LIMITS (flagged to Jose)
  This is theme-side code. It stops the opportunistic behaviour we are actually
  seeing -- customers clicking "+" in the cart -- but a determined attacker can
  still script /cart/change.js and hit /checkout before this runs. Hard
  enforcement needs a Shopify Function (Cart & Checkout Validation) plus a
  quantity cap on the "Welcome Kit" automatic discount in Admin.
*/
(function () {
  "use strict";

  if (window.ZenGiftGuard) return;

  /* Each family is one "offer". Entitlement is capped per family, so buying
     Kids Cafe AND ZenBean yields one gift set each, while a 3-pouch Kids Cafe
     bundle still yields exactly one Kids Cafe gift set. */
  var FAMILIES = [
    {
      key: "kids-cafe",
      label: "Kids Cafe welcome kit",
      // Any of these on a line means "this is a qualifying purchase".
      offerProps: ["_kids_cafe_welcome_kit", "_kids_cafe_pouch_index", "_kids_cafe_plan"],
      giftProps: ["_kids_cafe_welcome_gift"],
      // Hardcoded in kcpc-hero-buybox, kidscafe-v2-content and
      // zenbodi-home-v2-product-data. Kept here as a backstop so gift lines
      // added by an older cached script are still recognised.
      giftVariantIds: [52113525145911, 52113527570743, 52113527669047, 52113527701815]
    },
    {
      key: "zenbean",
      label: "ZenBean welcome kit",
      offerProps: ["_zenbean_welcome_kit"],
      giftProps: ["_zenbean_welcome_gift"],
      // ZenBean gift variants are picked per-section in the theme editor, so
      // there is no stable ID list to hardcode. Recognition is property-based,
      // including the legacy visible "Type" label below.
      giftVariantIds: [],
      legacyGiftLabel: "Free gift with your order"
    }
  ];

  var CART_MUTATION = /\/cart\/(add|change|update)(\.js)?(\?|$)/;
  var INTERNAL_HEADER = "X-Zen-Gift-Guard";
  // Captured before the wrapper below replaces window.fetch, so the guard's own
  // requests never re-enter it.
  var nativeFetch = window.fetch.bind(window);

  function props(item) {
    return (item && item.properties) || {};
  }

  function hasAnyProp(item, keys) {
    var bag = props(item);
    for (var i = 0; i < keys.length; i++) {
      var value = bag[keys[i]];
      if (value !== undefined && value !== null && value !== "") return true;
    }
    return false;
  }

  function isGiftLine(family, item) {
    if (hasAnyProp(item, family.giftProps)) return true;
    if (family.legacyGiftLabel && props(item).Type === family.legacyGiftLabel) return true;
    var variantId = Number(item && item.variant_id);
    for (var i = 0; i < family.giftVariantIds.length; i++) {
      if (family.giftVariantIds[i] === variantId) return true;
    }
    return false;
  }

  /* Gift-first: a line that qualifies as a gift is never also counted as the
     purchase that earns a gift. Without this, a doubled gift line could
     bootstrap its own entitlement. */
  function isOfferLine(family, item) {
    if (isGiftLine(family, item)) return false;
    return hasAnyProp(item, family.offerProps);
  }

  /* "One gift per offer": present-or-not, not per unit. Three pouches is still
     one welcome kit -- which is also how a single CTA click already behaves. */
  function entitlementFor(family, items) {
    for (var i = 0; i < items.length; i++) {
      if (isOfferLine(family, items[i])) return 1;
    }
    return 0;
  }

  function isGiftItem(item) {
    return FAMILIES.some(function (family) {
      return isGiftLine(family, item);
    });
  }

  /* ------------------------------------------------------------------ *
     Cart requests. Every internal request carries INTERNAL_HEADER so the
     fetch wrapper below can tell our own corrections apart from a caller's
     mutation and not recurse.
   * ------------------------------------------------------------------ */
  function requestJson(url, options) {
    var config = options || {};
    config.headers = Object.assign({ Accept: "application/json" }, config.headers || {});
    config.headers[INTERNAL_HEADER] = "1";

    return nativeFetch(url, config).then(function (response) {
      return response.json().then(
        function (json) {
          if (!response.ok) throw new Error((json && json.description) || "Cart request failed");
          return json;
        },
        function () {
          if (!response.ok) throw new Error("Cart request failed");
          return null;
        }
      );
    });
  }

  function fetchCart() {
    return requestJson("/cart.js");
  }

  function changeLine(lineKey, quantity) {
    return requestJson("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: lineKey, quantity: Math.max(0, Number(quantity) || 0) })
    });
  }

  /* Returns the corrections a cart needs, without applying them. Exported so
     the cart page can reason about the same rule when rendering. */
  function planCorrections(cart) {
    var items = (cart && cart.items) || [];
    var corrections = [];

    FAMILIES.forEach(function (family) {
      var allowance = entitlementFor(family, items);

      items.forEach(function (item) {
        if (!isGiftLine(family, item)) return;
        var quantity = Number(item.quantity) || 0;

        /* A gift carrying one of the underscore markers came from current
           theme code, so its family's offer markers are trustworthy and we can
           enforce the full allowance -- including dropping to 0 when the offer
           has been removed from the cart.

           A gift recognised only by a legacy signal (the visible "Type" label,
           or a known gift variant ID) may predate those markers. For those we
           cannot tell "offer was removed" from "cart made before the markers
           shipped", and silently deleting a real customer's gift is worse than
           leaving one behind -- so we only collapse duplicates down to one. */
        var trusted = hasAnyProp(item, family.giftProps);
        var cap = trusted ? allowance : Math.max(allowance, 1);

        if (quantity <= cap) return;
        corrections.push({
          family: family.key,
          key: item.key,
          title: item.product_title || item.title || "",
          from: quantity,
          to: cap,
          trusted: trusted
        });
      });
    });

    return corrections;
  }

  /* Applies corrections one at a time. Line keys survive /cart/change.js, so
     each correction is safe to issue against the keys planned up front. */
  function applyCorrections(cart, corrections) {
    var latest = cart;

    return corrections
      .reduce(function (chain, correction) {
        return chain.then(function () {
          return changeLine(correction.key, correction.to).then(function (updated) {
            if (updated) latest = updated;
          });
        });
      }, Promise.resolve())
      .then(function () {
        return latest;
      });
  }

  function announce(cart, corrections) {
    if (!corrections.length || !cart) return;
    try {
      document.dispatchEvent(
        new CustomEvent("zen:gift-guard:corrected", { detail: { cart: cart, corrections: corrections } })
      );
      document.dispatchEvent(new CustomEvent("zen:cart:updated", { detail: { cart: cart } }));
      var theme = window.ZenTheme;
      if (theme && typeof theme.updateCartCount === "function") theme.updateCartCount(cart.item_count);
      if (theme && typeof theme.renderCartDrawer === "function") theme.renderCartDrawer(cart);
    } catch (error) {
      /* Announcing is best-effort -- the cart itself is already correct. */
    }
  }

  function runPass() {
    return fetchCart()
      .then(function (cart) {
        var corrections = planCorrections(cart);
        if (!corrections.length) return cart;
        return applyCorrections(cart, corrections).then(function (finalCart) {
          announce(finalCart, corrections);
          return finalCart;
        });
      })
      .catch(function (error) {
        /* Never block the customer on a guard failure. Worst case we are back
           to today's behaviour, which the Admin-side discount cap must cover. */
        if (window.console && console.warn) console.warn("[zen-gift-guard] reconcile failed:", error);
        return null;
      });
  }

  /* Passes run strictly one after another. Sharing an in-flight pass would be
     wrong: a pass started before a mutation planned its corrections off the
     pre-mutation cart, so a caller joining it would be told "all clear" about
     a cart that pass never saw. */
  var queue = Promise.resolve();

  function reconcile() {
    var pass = queue.then(runPass, runPass);
    queue = pass.catch(function () {});
    return pass;
  }

  /* ------------------------------------------------------------------ *
     Interception. Every cart mutation in this theme goes through fetch, so
     wrapping it covers the six existing add-to-cart paths without touching
     them, plus any path added later.

     The wrapper AWAITS reconciliation before resolving. That matters: the
     buy-box CTAs do `.then(() => location.assign('/checkout'))`, so a
     background reconcile would race the redirect.
   * ------------------------------------------------------------------ */
  function isInternal(input, init) {
    var headers = (init && init.headers) || (input && input.headers);
    if (!headers) return false;
    if (typeof headers.get === "function") return Boolean(headers.get(INTERNAL_HEADER));
    return Boolean(headers[INTERNAL_HEADER]);
  }

  window.fetch = function (input, init) {
    var url = "";
    try {
      url = typeof input === "string" ? input : (input && input.url) || "";
    } catch (error) {
      url = "";
    }

    var promise = nativeFetch(input, init);
    if (isInternal(input, init) || !CART_MUTATION.test(url)) return promise;

    return promise.then(function (response) {
      if (!response || !response.ok) return response;
      // The response body is left untouched, so the caller still reads it.
      return reconcile().then(
        function () {
          return response;
        },
        function () {
          return response;
        }
      );
    });
  };

  /* ------------------------------------------------------------------ *
     Checkout gate. Last line of defence for carts inflated before this
     script shipped, or in a tab that has been open a while.
   * ------------------------------------------------------------------ */
  var CHECKOUT_TRIGGER = 'a[href*="/checkout"], [name="checkout"], [data-zen-cart-checkout], [data-zen-checkout]';

  var gating = false;

  function gateCheckout(trigger, event) {
    if (!trigger || trigger.hasAttribute("data-zen-gift-guard-cleared")) return;

    // Swallow impatient double-clicks rather than starting a second pass.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (gating) return;
    gating = true;

    var isButton = trigger.tagName === "BUTTON";
    var label = trigger.textContent;
    if (isButton) {
      trigger.disabled = true;
      trigger.textContent = "Checking out…";
    }

    reconcile().then(function () {
      gating = false;
      if (isButton) {
        trigger.disabled = false;
        trigger.textContent = label;
      }
      /* Replay the real click instead of navigating to a hardcoded /checkout.
         That preserves whatever the element actually does -- an <a> keeps its
         full href including ?discount=, a name="checkout" button still POSTs
         the cart form -- and lets analytics listeners fire normally. The flag
         makes this second click pass straight through the gate. */
      trigger.setAttribute("data-zen-gift-guard-cleared", "true");
      trigger.click();
    });
  }

  document.addEventListener(
    "click",
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      gateCheckout(target.closest(CHECKOUT_TRIGGER), event);
    },
    true
  );

  document.addEventListener(
    "submit",
    function (event) {
      var submitter = event.submitter;
      if (!submitter || submitter.name !== "checkout") return;
      gateCheckout(submitter, event);
    },
    true
  );

  window.ZenGiftGuard = {
    reconcile: reconcile,
    planCorrections: planCorrections,
    isGiftItem: isGiftItem,
    fetchCart: fetchCart,
    families: FAMILIES
  };

  /* Sweep once on load so carts already inflated by the old behaviour are
     corrected the next time the customer visits any page. */
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", reconcile);
  } else {
    reconcile();
  }
})();
